import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organisation: '',
    enquiryType: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '0330 043 3693',
      link: 'tel:03300433693'
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'info@threesphereshealthcare.org',
      link: 'mailto:info@threesphereshealthcare.org'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'United Kingdom',
      link: null
    },
    {
      icon: Clock,
      title: 'Response Time',
      value: 'Within 24-48 hours',
      link: null
    }
  ];

  const enquiryTypes = [
    { value: 'general', label: 'General Enquiry' },
    { value: 'referral', label: 'Make a Referral' },
    { value: 'staffing', label: 'Staffing Request' },
    { value: 'recruitment', label: 'Job Application' },
    { value: 'partnership', label: 'Partnership Opportunity' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      
      // Reset form
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          organisation: '',
          enquiryType: 'general',
          message: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="heading-hero">Contact Us</h1>
            <p className="body-large">
              Get in touch to discuss referrals, staffing needs, partnership opportunities, or to join our team.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="p-6 text-center border-0 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">{info.title}</h3>
                  {info.link ? (
                    <a 
                      href={info.link}
                      className="text-sm text-[#4A90E2] hover:underline"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-600">{info.value}</p>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="p-8 md:p-12 border-0 shadow-xl">
              <h2 className="heading-2 mb-2 text-center">Send Us a Message</h2>
              <p className="body-medium text-center text-gray-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="border-gray-300 focus:border-[#4A90E2]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.smith@example.com"
                      className="border-gray-300 focus:border-[#4A90E2]"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="07XXX XXXXXX"
                      className="border-gray-300 focus:border-[#4A90E2]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organisation">Organisation</Label>
                    <Input
                      id="organisation"
                      name="organisation"
                      type="text"
                      value={formData.organisation}
                      onChange={handleChange}
                      placeholder="Your organisation name"
                      className="border-gray-300 focus:border-[#4A90E2]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="enquiryType">Enquiry Type *</Label>
                  <select
                    id="enquiryType"
                    name="enquiryType"
                    required
                    value={formData.enquiryType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent"
                  >
                    {enquiryTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please provide details about your enquiry..."
                    rows={6}
                    className="border-gray-300 focus:border-[#4A90E2] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || isSubmitted}
                  className="w-full bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] hover:shadow-lg transition-all"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle className="mr-2" size={20} />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting this form, you agree to our privacy policy and data protection practices.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Alternative Contact Methods */}
      <section className="section bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Prefer to Contact Us Directly?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 text-center">
                <Phone className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="heading-3 mb-3">Call Us</h3>
                <p className="body-medium text-gray-700 mb-4">
                  Speak directly with our team
                </p>
                <a 
                  href="tel:03300433693"
                  className="text-[#4A90E2] font-semibold text-lg hover:underline"
                >
                  0330 043 3693
                </a>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 text-center">
                <Mail className="w-12 h-12 text-[#7B68BE] mx-auto mb-4" />
                <h3 className="heading-3 mb-3">Email Us</h3>
                <p className="body-medium text-gray-700 mb-4">
                  Send us an email anytime
                </p>
                <a 
                  href="mailto:info@threesphereshealthcare.org"
                  className="text-[#7B68BE] font-semibold text-sm hover:underline break-all"
                >
                  info@threesphereshealthcare.org
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
